import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import FilterBar from './FilterBar';
import ColumnToggle from './ColumnToggle';
import ExportButton from './ExportButton';
import type { SalesforceFieldDescribe } from '../types/salesforce';
import { formatFieldValue } from '../utils/formatters';
import StatusBadge from './StatusBadge';
import { SF_BASE } from '../constants';

// Fields that should render as status badges
const STATUS_FIELDS = new Set([
  'Plan_Status__c',
  'EEP_Order_Status__c',
  'StageName',
  'Status',
  'Status__c',
  'Plan_Credit_Used__c',
  'Include_Attorney_Review_Letter__c',
  'Include_Attorney_Review_Letter_Call__c',
  'Include_Mobile_Notary_Service__c',
  'Include_Print_Bind_Tab_Ship_Service__c',
  'Mobile_Notary_Service_Used__c',
  'Print_Bind_Tab_Ship_Service_Used__c',
  'Is_Plan_Amendment__c',
  'Have_Previous_Trust__c',
]);

interface DataTableProps {
  data: Record<string, unknown>[];
  columns?: ColumnDef<Record<string, unknown>>[];
  fieldDescribes?: SalesforceFieldDescribe[];
  loading?: boolean;
  error?: string | null;
  exportFilename?: string;
  sfBaseUrl?: string;
  nameField?: string;
  /** Fields to show by default when using auto-generated columns */
  defaultVisibleFields?: string[];
  /** Column IDs to hide by default (user can toggle them on via column picker) */
  initialHiddenColumns?: string[];
  onRefetch?: () => void;
}

function buildAutoColumns(
  data: Record<string, unknown>[],
  fieldDescribes: SalesforceFieldDescribe[],
  sfBaseUrl: string,
  nameField: string
): ColumnDef<Record<string, unknown>>[] {
  if (data.length === 0 && fieldDescribes.length === 0) return [];

  // Build a map from field name → describe
  const describeMap = new Map(fieldDescribes.map((f) => [f.name, f]));

  // Get all keys from data
  const allKeys = new Set<string>();
  for (const row of data) {
    for (const key of Object.keys(row)) {
      if (key !== 'attributes') allKeys.add(key);
    }
  }

  return Array.from(allKeys).map((key): ColumnDef<Record<string, unknown>> => {
    const fd = describeMap.get(key);
    const label = fd?.label || key.replace(/__c$/g, '').replace(/_/g, ' ');

    const isNameField = key === nameField || key === 'Name';
    const isStatusField = STATUS_FIELDS.has(key) || fd?.type === 'boolean';

    return {
      id: key,
      accessorKey: key,
      header: label,
      cell: ({ row }) => {
        const value = row.getValue(key);

        // Name field → clickable SF link
        if (isNameField && sfBaseUrl) {
          const id = row.original.Id as string;
          return (
            <a
              href={`${sfBaseUrl}/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              {String(value || '')}
            </a>
          );
        }

        // Status badge fields
        if (isStatusField) {
          return <StatusBadge value={value as string} />;
        }

        // URL fields
        if (fd?.type === 'url' && value) {
          return (
            <a
              href={String(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline truncate block max-w-xs"
            >
              {String(value)}
            </a>
          );
        }

        return <span>{formatFieldValue(value, fd?.type)}</span>;
      },
      sortingFn: fd?.type === 'currency' || fd?.type === 'double' || fd?.type === 'int' || fd?.type === 'percent'
        ? 'basic'
        : 'alphanumeric',
      enableGlobalFilter: true,
    };
  });
}

export default function DataTable({
  data,
  columns: explicitColumns,
  fieldDescribes = [],
  loading,
  error,
  exportFilename = 'export',
  sfBaseUrl = SF_BASE,
  nameField = 'Name',
  defaultVisibleFields,
  initialHiddenColumns,
  onRefetch,
}: DataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    if (initialHiddenColumns) {
      const vis: VisibilityState = {};
      for (const id of initialHiddenColumns) vis[id] = false;
      return vis;
    }
    if (!defaultVisibleFields) return {};
    const vis: VisibilityState = {};
    return vis;
  });
  const [pageSize, setPageSize] = useState(25);

  const columns = useMemo(() => {
    if (explicitColumns) return explicitColumns;
    return buildAutoColumns(data, fieldDescribes, sfBaseUrl, nameField);
  }, [explicitColumns, data, fieldDescribes, sfBaseUrl, nameField]);

  // Set initial visibility when columns are ready and defaultVisibleFields is provided
  useMemo(() => {
    if (defaultVisibleFields && columns.length > 0 && Object.keys(columnVisibility).length === 0) {
      const vis: VisibilityState = {};
      const visSet = new Set(defaultVisibleFields);
      for (const col of columns) {
        const id = col.id || (col as { accessorKey?: string }).accessorKey || '';
        if (id && !visSet.has(id)) {
          vis[id] = false;
        }
      }
      setColumnVisibility(vis);
    }
  }, [defaultVisibleFields, columns]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      pagination: { pageIndex: 0, pageSize },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-eep-navy rounded-full" />
          <p className="text-sm text-gray-500">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8">
        <div className="text-center">
          <p className="text-red-600 font-medium">Error loading data</p>
          <p className="text-sm text-red-400 mt-1">{error}</p>
          {onRefetch && (
            <button
              onClick={onRefetch}
              className="mt-4 px-4 py-2 text-sm bg-eep-navy text-white rounded-lg hover:bg-eep-navy-light"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <FilterBar
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          totalRows={data.length}
          filteredRows={table.getFilteredRowModel().rows.length}
        />
        <div className="flex items-center gap-2">
          <ColumnToggle table={table} />
          <ExportButton table={table} filename={exportFilename} />
          {onRefetch && (
            <button
              onClick={onRefetch}
              className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-gray-100">
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span className="inline-flex flex-col">
                        <ChevronUpIcon
                          className={`w-3 h-3 -mb-0.5 ${
                            header.column.getIsSorted() === 'asc'
                              ? 'text-eep-navy'
                              : 'text-gray-300'
                          }`}
                        />
                        <ChevronDownIcon
                          className={`w-3 h-3 -mt-0.5 ${
                            header.column.getIsSorted() === 'desc'
                              ? 'text-eep-navy'
                              : 'text-gray-300'
                          }`}
                        />
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getVisibleLeafColumns().length}
                  className="px-4 py-12 text-center text-sm text-gray-400"
                >
                  No data found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              table.setPageSize(Number(e.target.value));
            }}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            {[25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount() || 1}
          </span>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
